import { AxiosResponse } from "axios";
import { authAxios as axios } from "shared-auth";

import { BreakdownId } from "shared/models/breakdown";
import { FollowUpId } from "shared/models/followUp";

import config from "config/global";

import { IJobFilterParams } from "models/api";
import {
  InteractionActionType,
  IPaginatedJobItemList,
  IReportConcernData,
  JobItemType,
} from "models/job";

export type InteractionItemId = string;

export interface IInteractionItem {
  id: InteractionItemId;
  jobItemType: JobItemType;
}

export type SetJobActionType = (
  interactions: IInteractionItem[]
) => Promise<AxiosResponse<void>>;

class JobService {
  private readonly url: string;
  private readonly userInteractionApiUrl: string;

  constructor({ url }: { url: string }) {
    this.url = url;
    this.userInteractionApiUrl = `${url}/userinteraction`;
  }

  public generateInteractionItemId(
    jobItemType: JobItemType.Linkmail,
    breakdownId: BreakdownId
  ): string;
  public generateInteractionItemId(
    jobItemType: JobItemType.Role,
    breakdownId: BreakdownId,
    roleId: string
  ): string;
  public generateInteractionItemId(
    jobItemType: JobItemType.FollowUp,
    followUpId: FollowUpId
  ): string;
  public generateInteractionItemId(
    jobItemType: JobItemType,
    breakdownOrFollowUpId: BreakdownId,
    roleId?: string
  ): string {
    switch (jobItemType) {
      case JobItemType.Role:
        return `${breakdownOrFollowUpId}_${roleId}`;
      default:
        return breakdownOrFollowUpId;
    }
  }

  public async find(
    params: IJobFilterParams,
    abortController?: AbortController
  ): Promise<IPaginatedJobItemList> {
    const response = await axios(`${this.url}/search`, {
      method: "post",
      data: params,
      signal: abortController?.signal,
    });

    return response.data;
  }

  public async getCounts(
    filters: IJobFilterParams[],
    abortController?: AbortController
  ): Promise<number[]> {
    const response = await axios(`${this.url}/count`, {
      method: "post",
      data: filters,
      signal: abortController?.signal,
    });

    return response.data;
  }

  public async reportConcern(
    data: IReportConcernData,
    abortController?: AbortController
  ): Promise<void> {
    await axios(`${this.url}/reportconcern`, {
      method: "post",
      data,
      signal: abortController?.signal,
    });
  }

  public markAsRead: SetJobActionType = (interactions) => {
    return this.setActionType(interactions, InteractionActionType.Add);
  };

  public markAsUnread: SetJobActionType = (interactions) => {
    return this.setActionType(interactions, InteractionActionType.Remove);
  };

  private async setActionType(
    interactions: IInteractionItem[],
    actionType: InteractionActionType
  ) {
    return axios(this.userInteractionApiUrl, {
      method: "post",
      data: { interactions, actionType },
    });
  }
}

export default new JobService({
  url: config.jobsApiUrl,
});
